import * as React from 'react'
import { cn } from '../../lib/utils'
import { textareaVariants, type TextareaProps } from './textarea'
import { useFieldStatus } from './field'

/**
 * AutoGrowTextarea — a `Textarea` that grows to fit its content instead of
 * scrolling. Shares the exact `Textarea` look (border, focus ring, status,
 * sizes) so it drops in anywhere a textarea would.
 *
 *   <AutoGrowTextarea placeholder="Write a comment…" />
 *
 * Implemented with the CSS-grid replica technique, not JS height math: the
 * textarea and an invisible `whitespace-pre-wrap` mirror of its value share one
 * grid cell, so the cell (and the textarea) take the mirror's height. This sizes
 * correctly on the first render — no `useEffect`, no layout flash, SSR-safe.
 *
 * Controlled (`value` + `onChange`) or uncontrolled (`defaultValue`). The mirror
 * needs the current text, so an uncontrolled instance tracks it internally.
 */
export interface AutoGrowTextareaProps extends TextareaProps {}

export const AutoGrowTextarea = React.forwardRef<
  HTMLTextAreaElement,
  AutoGrowTextareaProps
>(
  (
    { className, status, size, rows = 2, value, defaultValue, onChange, ...props },
    ref
  ) => {
    const resolved = useFieldStatus(status ?? undefined)
    const isControlled = value !== undefined
    const [internal, setInternal] = React.useState(
      defaultValue?.toString() ?? ''
    )
    const current = isControlled ? String(value ?? '') : internal

    // Same classes on both nodes so the mirror's box matches the textarea's
    // (font, padding, border, line-height) and its height drives the cell.
    const shared = textareaVariants({ status: resolved, size, className })

    return (
      <div className="grid w-full">
        <textarea
          ref={ref}
          rows={rows}
          aria-invalid={resolved === 'error' || undefined}
          value={isControlled ? value : undefined}
          defaultValue={isControlled ? undefined : defaultValue}
          onChange={(e) => {
            if (!isControlled) setInternal(e.target.value)
            onChange?.(e)
          }}
          className={cn(shared, 'resize-none overflow-hidden [grid-area:1/1]')}
          {...props}
        />
        <div
          aria-hidden="true"
          className={cn(
            shared,
            'invisible whitespace-pre-wrap break-words [grid-area:1/1]'
          )}
        >
          {/* Trailing space so a final newline still expands the mirror. */}
          {current + ' '}
        </div>
      </div>
    )
  }
)
AutoGrowTextarea.displayName = 'AutoGrowTextarea'
